import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CenterFrameLayout } from 'Layouts';
import { useSubscription } from 'Hooks/useSubscription';
import './BangoFinal.css';
import { addBangoSubscription } from 'Api/AccountApi';
import { getEntitlements } from 'State/User/UserActions';
import { StaticLinkButton } from 'Components/Fields/Buttons';

export const BangoFinal = () => {
  const dispatch = useDispatch();
  const {
    routes,
    defaultConfig: { rootDomain },
  } = useSelector((s) => s.appConfig);
  const { subscriptionId, redirectUrl } = useSelector((s) => s.integrations.bango);
  const [view, setView] = useState('LOADING'); // 'LOADING' 'SUCCESS' 'INELIGIBLE' 'ERROR'
  const [addBangoSuccess, setAddBangoSuccess] = useState(false);

  const { isStatusSuccess, hasActiveSubscription, isPendingCancel, purchaseType } = useSubscription();

  //Including rootDomain so reloads site and pulls params again after sign out
  //URL comes in encoded from Verizon Bango, so encoding it again.
  const createAccountUrl = `${routes.signout}?return_to=${rootDomain}${
    routes.signupCreateAccount
  }?subscriptionRequestId=${subscriptionId}&redirect_uri=${encodeURIComponent(redirectUrl)}`;

  const text = {
    LOADING: {
      SHOW_LOADER: true,
      TITLE: 'Checking your subscription', //same title as in withSubscription HOC when loading
    },
    SUCCESS: {
      TITLE: 'Congratulations!',
      BODY: 'Your account has been successfully activated with Verizon +play subscription. Enjoy Fox Nation!',
      PRIMARY_BUTTON: 'Continue to Verizon',
      PRIMARY_PATH: redirectUrl,
    },
    INELIGIBLE: {
      TITLE: 'Good News!',
      BODY: 'It looks like you are already a valued Fox Nation subscriber. If you would like to purchase an additional subscription through Verizon +play, please use a new email address.',
      PRIMARY_BUTTON: 'Yes, take me back to Create Account',
      PRIMARY_PATH: createAccountUrl,
      SECONDARY_BUTTON: 'No, take me back to Verizon +play',
      SECONDARY_PATH: redirectUrl,
    },
    ERROR: {
      TITLE: 'Sorry!',
      BODY: 'Something went wrong.',
      PRIMARY_BUTTON: 'Take me back to Verizon',
      PRIMARY_PATH: redirectUrl,
    },
  };

  const addBango = useCallback(async () => {
    const { isSuccess } = await addBangoSubscription(subscriptionId);
    if (isSuccess) {
      setAddBangoSuccess(true);
      // Getting entitlements shouldn't be necessary since user will be going to Verizon after sign up
      // Doing it just to be extra safe
      dispatch(getEntitlements());
    } else {
      setView('ERROR');
    }
  }, [dispatch, subscriptionId]);

  useEffect(() => {
    if (isStatusSuccess) {
      const hasBangoSub = addBangoSuccess || purchaseType === 'bango';
      if (hasBangoSub) {
        setView('SUCCESS');
      } else if (hasActiveSubscription || isPendingCancel) {
        setView('INELIGIBLE');
      } else if (subscriptionId && !hasBangoSub) {
        addBango();
      } else {
        setView('ERROR');
      }
    }
  }, [
    dispatch,
    isStatusSuccess,
    hasActiveSubscription,
    subscriptionId,
    purchaseType,
    addBangoSuccess,
    isPendingCancel,
    addBango,
  ]);

  return (
    <CenterFrameLayout title={text[view].TITLE} showLoader={text[view].SHOW_LOADER} modernLayout>
      <div>
        <div className="bangoMainText">{text[view].BODY}</div>
        {!!text[view].PRIMARY_BUTTON && !!text[view].PRIMARY_PATH && (
          <StaticLinkButton
            className="bangoPrimaryButton"
            path={text[view].PRIMARY_PATH}
            text={text[view].PRIMARY_BUTTON}
          />
        )}
        {!!text[view].SECONDARY_BUTTON && !!text[view].SECONDARY_PATH && (
          <a className="bangoSecondaryButton" href={text[view].SECONDARY_PATH}>
            {text[view].SECONDARY_BUTTON}
          </a>
        )}
      </div>
    </CenterFrameLayout>
  );
};
